import { useCallback, useEffect, useState } from 'react';

/**
 * Elastic overscroll cues for platforms without native rubber-band.
 *
 * Native behaviour this must not fight (double-bounce is the failure mode):
 * - iOS/iPadOS (incl. desktop-mode iPad masquerading as Mac): WebKit bounces
 *   the page and every overflow scroller. We no-op entirely there.
 * - macOS: every browser bounces the page (root scroller), and Safari
 *   additionally bounces overflow scrollers. Page hook no-ops on macOS;
 *   carousel hook no-ops only on macOS Safari.
 * - Windows/Linux/Android: nothing bounces (Android shows an edge glow for
 *   touch instead). Our wheel-driven cue applies.
 *
 * Mechanism: instead of transforms (which would repurpose `position: fixed`
 * descendants like the bottom nav into containing-block anchored elements),
 * we inject a few px of padding at the reached scroll end and let the native
 * scroll action move into that space, so motion stays fully native and
 * scroll chaining behaves exactly as before. On release the space decays with
 * an ease-out curve while the offset is clamped, giving the spring-back.
 *
 * Wheel-only by design: touch platforms either bounce natively (iOS) or get
 * the platform glow/chaining (Android), so no touchmove handling is needed.
 */

/** Subtle on purpose: a few px, approached with resistance (gain << 1). */
const MAX_SPACE_PX = 8;
const GAIN = 0.1;
/** Exponential ease-out time constant for the spring-back. */
const RELEASE_TAU_MS = 110;
/** Wheel silence before the spring-back starts. */
const IDLE_MS = 140;
/** Firefox reports wheel deltas in lines; ~16px per line. */
const LINE_PX = 16;

export interface ElasticSupport {
  page: boolean;
  overflow: boolean;
}

/** Exported for tests: `ua`/`maxTouchPoints` injectable. */
export function nativeElasticSupport(
  ua: string = typeof navigator === 'undefined' ? '' : navigator.userAgent,
  maxTouchPoints: number = typeof navigator === 'undefined' ? 0 : navigator.maxTouchPoints
): ElasticSupport {
  // iPadOS 13+ in desktop mode reports as Macintosh with touch points.
  const isAppleTouch = /iP(hone|od|ad)/.test(ua) || (/Macintosh/.test(ua) && maxTouchPoints > 1);
  if (isAppleTouch) return { page: true, overflow: true };
  if (/Macintosh/.test(ua)) {
    // macOS Safari bounces overflow scrollers too; Chrome/Firefox only the page.
    const isSafari =
      /AppleWebKit/.test(ua) && !/Chrom(e|ium)|Edg\//.test(ua) && !/Firefox|FxiOS/.test(ua);
    return { page: true, overflow: isSafari };
  }
  return { page: false, overflow: false };
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false)
  );
}

/**
 * Structural subset of the DOM wheel event: the repo's eslint globals do not
 * whitelist the WheelEvent type name, and the controller needs only these.
 */
interface WheelDelta {
  deltaX: number;
  deltaY: number;
  deltaMode: number;
  ctrlKey: boolean;
}

/** Exported for tests: normalizes a wheel event to pixels on one axis. */
export function wheelPixelDelta(delta: number, deltaMode: number): number {
  return deltaMode === 1 ? delta * LINE_PX : delta;
}

export interface ScrollEdge {
  /** Current scroll offset (scrollLeft/scrollTop). */
  pos: number;
  /** Max natural offset excluding injected space (scrollWidth/Height - client). */
  max: number;
}

/** Exported for tests: jsdom can stub scrollWidth/scrollLeft etc. */
export function scrollEdge(scroller: HTMLElement, axis: 'x' | 'y'): ScrollEdge {
  return axis === 'x'
    ? { pos: scroller.scrollLeft, max: scroller.scrollWidth - scroller.clientWidth }
    : { pos: scroller.scrollTop, max: scroller.scrollHeight - scroller.clientHeight };
}

/**
 * Exported for tests: space growth for a wheel delta at an edge, with
 * resistance (gain) and a hard subtle cap. `space` is the amount already
 * injected at that edge. Returns the signed growth to apply.
 */
export function elasticGrowth(
  deltaPx: number,
  space: number,
  maxPx: number = MAX_SPACE_PX
): number {
  const room = maxPx - space;
  if (room <= 0 || deltaPx === 0) return 0;
  const wanted = Math.abs(deltaPx) * GAIN;
  return Math.sign(deltaPx) * Math.min(wanted, room);
}

/**
 * Exported for tests: one ease-out release step. Returns the remaining space.
 * Exponential decay toward 0; monotonic, never negative.
 */
export function releaseStep(space: number, dtMs: number, tauMs: number = RELEASE_TAU_MS): number {
  if (space <= 0 || dtMs <= 0) return 0;
  const next = space * Math.exp(-dtMs / tauMs);
  return next < 0.2 ? 0 : next;
}

const paddingProp = (axis: 'x' | 'y', edge: 'start' | 'end'): string =>
  axis === 'x'
    ? edge === 'start'
      ? 'padding-left'
      : 'padding-right'
    : edge === 'start'
      ? 'padding-top'
      : 'padding-bottom';

export interface ElasticController {
  /** Wheel handler. True when the event engaged/extended the elastic cue. */
  handleWheel: (e: WheelDelta) => boolean;
  /** Current injected space (px, always >= 0). */
  space: () => number;
  /** Which edge holds the space: -1 start, +1 end, 0 none. */
  edge: () => -1 | 0 | 1;
  /** One frame of the ease-out spring-back. Returns true when fully released. */
  stepRelease: (dtMs: number) => boolean;
  /** Snap-release everything (reduced motion, teardown). */
  releaseNow: () => void;
  destroy: () => void;
}

/**
 * Framework-free core so the spring math is unit-testable without rAF.
 * The scroller is what scrolls; the host is what receives the padding
 * (the element itself for carousels, `<main>` for the page).
 */
export function createElasticController(
  scroller: HTMLElement,
  axis: 'x' | 'y',
  host: HTMLElement = scroller
): ElasticController {
  let space = 0;
  let edgeDir: -1 | 0 | 1 = 0;

  const clampOffset = () => {
    if (edgeDir !== 1) return;
    const { pos, max } = scrollEdge(scroller, axis);
    if (pos > max) {
      if (axis === 'x') scroller.scrollLeft = max;
      else scroller.scrollTop = max;
    }
  };

  const setSpace = (next: number, dir: -1 | 0 | 1) => {
    space = Math.max(0, next);
    edgeDir = space > 0 ? dir : 0;
    if (edgeDir === 0 && next <= 0) {
      host.style.removeProperty(paddingProp(axis, 'start'));
      host.style.removeProperty(paddingProp(axis, 'end'));
      return;
    }
    // Keep the inactive edge clean so direction flips never stack padding.
    host.style.setProperty(paddingProp(axis, edgeDir === -1 ? 'start' : 'end'), `${space}px`);
    host.style.setProperty(paddingProp(axis, edgeDir === -1 ? 'end' : 'start'), '0px');
    clampOffset();
  };

  const controller: ElasticController = {
    handleWheel(e) {
      if (e.ctrlKey) return false; // pinch-zoom, never a scroll intent
      if (prefersReducedMotion()) {
        if (space > 0) controller.releaseNow();
        return false;
      }
      const primary = axis === 'x' ? e.deltaX : e.deltaY;
      const other = axis === 'x' ? e.deltaY : e.deltaX;
      if (primary === 0 || Math.abs(other) > Math.abs(primary)) return false;
      const deltaPx = wheelPixelDelta(primary, e.deltaMode);
      const { pos, max } = scrollEdge(scroller, axis);
      // Scroll-snap strips with leading padding rest at pos = padding (not 0),
      // so the start boundary includes the host's leading padding.
      const leadPad =
        Number.parseFloat(
          axis === 'x'
            ? globalThis.getComputedStyle(host).paddingLeft
            : globalThis.getComputedStyle(host).paddingTop
        ) || 0;
      // max === 0 (content fits) still counts as both edges: a short strip or
      // page gets the same tug without becoming a native scroll container.
      const dir: -1 | 1 | null =
        deltaPx < 0 && pos <= leadPad + 1 ? -1 : deltaPx > 0 && pos >= max - 1 ? 1 : null;
      if (dir === null) return false;
      if (dir !== edgeDir && space > 0) setSpace(0, 0); // flip edges cleanly
      const growth = elasticGrowth(deltaPx, space);
      if (growth !== 0) setSpace(space + Math.abs(growth), dir);
      else if (edgeDir === 0) setSpace(0, dir);
      return true;
    },
    space: () => space,
    edge: () => edgeDir,
    stepRelease(dtMs) {
      if (prefersReducedMotion()) {
        controller.releaseNow();
        return true;
      }
      if (space <= 0) return true;
      setSpace(releaseStep(space, dtMs), edgeDir);
      return space <= 0;
    },
    releaseNow() {
      setSpace(0, 0);
    },
    destroy() {
      controller.releaseNow();
    },
  };
  return controller;
}

export interface ElasticOptions {
  axis?: 'x' | 'y';
  idleMs?: number;
  /** Padding recipient; defaults to the scroller (page-level passes main). */
  host?: HTMLElement;
}

/**
 * Attaches the wheel-driven elastic cue. Returns a cleanup. The release loop
 * runs only while space > 0, so steady-state cost is a single passive wheel
 * listener.
 */
export function attachElasticOverscroll(
  scroller: HTMLElement,
  options: ElasticOptions = {}
): () => void {
  const axis = options.axis ?? 'x';
  const idleMs = options.idleMs ?? IDLE_MS;
  const controller = createElasticController(scroller, axis, options.host ?? scroller);
  let releaseFrame: number | null = null;
  let idleTimer: number | null = null;
  let lastTs = 0;

  const stopRelease = () => {
    if (releaseFrame !== null) {
      window.cancelAnimationFrame(releaseFrame);
      releaseFrame = null;
    }
  };

  const releaseLoop = (ts: number) => {
    const dt = lastTs === 0 ? 16 : Math.min(ts - lastTs, 64);
    lastTs = ts;
    if (controller.stepRelease(dt)) {
      releaseFrame = null;
      lastTs = 0;
      return;
    }
    releaseFrame = window.requestAnimationFrame(releaseLoop);
  };

  const scheduleRelease = () => {
    if (idleTimer !== null) window.clearTimeout(idleTimer);
    idleTimer = window.setTimeout(() => {
      idleTimer = null;
      stopRelease();
      if (controller.space() > 0) releaseFrame = window.requestAnimationFrame(releaseLoop);
    }, idleMs);
  };

  const onWheel = (e: WheelDelta) => {
    stopRelease();
    controller.handleWheel(e);
    scheduleRelease();
  };

  scroller.addEventListener('wheel', onWheel, { passive: true });
  return () => {
    stopRelease();
    if (idleTimer !== null) window.clearTimeout(idleTimer);
    scroller.removeEventListener('wheel', onWheel);
    controller.destroy();
  };
}

/**
 * Carousel hook: attach the returned ref to a native horizontal scroller
 * (overflow-x: auto). No-ops on platforms with native overflow rubber-band
 * (iOS everywhere, macOS Safari) and under prefers-reduced-motion.
 *
 * Callback ref (not ref-object + effect-once) so scrollers that mount after
 * their parent (e.g. the composer image strip that appears on first upload)
 * still get wired when they appear.
 */
export function useElasticOverscroll<T extends HTMLElement>(): React.Ref<T> {
  const [node, setNode] = useState<T | null>(null);
  const ref = useCallback((n: T | null) => setNode(n), []);
  useEffect(() => {
    if (!node || nativeElasticSupport().overflow) return;
    return attachElasticOverscroll(node, { axis: 'x' });
  }, [node]);
  return ref;
}

/**
 * Page-level hook: mount once (client entry) so every route gets the elastic
 * cue when wheeling past the document's ends, including pages shorter than
 * the viewport. Space is injected as padding on the main landmark, keeping
 * fixed/sticky overlays untouched.
 */
export function attachPageElasticOverscroll(): () => void {
  if (nativeElasticSupport().page) return () => {};
  const scroller = document.scrollingElement;
  const main = document.getElementById('main-content');
  if (!scroller || !main) return () => {};
  return attachElasticOverscroll(scroller as HTMLElement, { axis: 'y', host: main });
}
